import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, animate } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "../lib/i18n";
import { RevealLines } from "./Reveal";

const smooth = (t) => t * t * (3 - 2 * t);

const HeroCursor = ({ cx, cy, side, visible }) => {
  const { t } = useLang();
  const x = useSpring(cx, { stiffness: 400, damping: 35 });
  const y = useSpring(cy, { stiffness: 400, damping: 35 });
  const street = side === "street";
  return (
    <motion.div
      style={{ x, y, opacity: visible ? 1 : 0 }}
      className="hidden md:flex pointer-events-none absolute top-0 left-0 z-30 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-300"
    >
      <div className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors duration-500 ${street ? "bg-[var(--acid)] text-black f-mono text-[11px] uppercase tracking-[0.2em]" : "bg-[var(--ivory)] text-[var(--navy)] f-serif italic text-[15px]"}`}>
        {street && <ArrowLeft size={13} />}
        {street ? t("enterStreet") : t("enterOld")}
        {!street && <ArrowRight size={13} />}
      </div>
    </motion.div>
  );
};

const Layer = ({ src, alt, px, py, clip, children, testid }) => (
  <motion.div data-testid={testid} className="absolute inset-0 overflow-hidden" style={clip ? { clipPath: clip } : undefined}>
    <motion.img
      src={src}
      alt={alt}
      draggable={false}
      style={{ x: px, y: py, scale: 1.08 }}
      className="absolute inset-0 w-full h-full object-cover select-none"
    />
    {children}
  </motion.div>
);

export const SplitHero = ({ ready }) => {
  const { t } = useLang();
  const nav = useNavigate();
  const ref = useRef(null);
  const split = useMotionValue(50);
  const s = useSpring(split, { stiffness: 70, damping: 20, mass: 0.7 });
  const clip = useTransform(s, (v) => `inset(0 0 0 ${v}%)`);
  const seam = useTransform(s, (v) => `${v}%`);
  const streetFull = useTransform(s, [50, 95], [0, 1]);
  const oldFull = useTransform(s, [50, 5], [0, 1]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(mx, { stiffness: 50, damping: 20 });
  const py = useSpring(my, { stiffness: 50, damping: 20 });
  const cx = useMotionValue(0);
  const cy = useMotionValue(0);
  const [side, setSide] = useState("street");
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!ready) return undefined;
    const c = animate(split, [50, 60, 40, 50], { duration: 2.6, ease: "easeInOut", delay: 0.6 });
    return () => c.stop();
  }, [ready, split]);

  const update = (clientX, clientY) => {
    const r = ref.current.getBoundingClientRect();
    const p = (clientX - r.left) / r.width;
    const d = p - 0.5;
    const k = smooth(Math.min(1, Math.abs(d) / 0.36));
    split.set(50 + (d < 0 ? 1 : -1) * k * 50);
    mx.set((0.5 - p) * 28);
    my.set((0.5 - (clientY - r.top) / r.height) * 18);
    cx.set(clientX - r.left);
    cy.set(clientY - r.top);
    const next = p < 0.5 ? "street" : "oldmoney";
    if (next !== side) setSide(next);
  };

  const go = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    nav(p < s.get() ? "/collections/streetwear" : "/collections/old-money");
  };

  return (
    <section
      ref={ref}
      data-testid="hero-split-container"
      className="relative h-[100svh] w-full overflow-hidden bg-black md:cursor-none select-none"
      onMouseMove={(e) => update(e.clientX, e.clientY)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); split.set(50); mx.set(0); my.set(0); }}
      onTouchMove={(e) => update(e.touches[0].clientX, e.touches[0].clientY)}
      onClick={(e) => go(e.clientX)}
    >
      <Layer testid="hero-split-left-streetwear" src="/images/hero_street.jpg" alt="ANVOCRA streetwear — brutalist beton" px={px} py={py}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
        <motion.div style={{ opacity: streetFull }} className="absolute inset-0 bg-[#0b0c0e]/25" />
        <div className="absolute left-5 md:left-10 bottom-24 md:bottom-14 md:max-w-[46vw]">
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-[var(--acid)] mb-4 flicker">● {t("street")} / 01</p>
          {ready && (
            <RevealLines
              inView={false}
              delay={0.2}
              lines={[t("heroStreet1"), t("heroStreet2"), t("heroStreet3")]}
              className="f-street uppercase text-white leading-[0.86] text-[9.5vw] sm:text-6xl lg:text-[7.5rem]"
            />
          )}
        </div>
      </Layer>

      <Layer testid="hero-split-right-oldmoney" src="/images/hero_oldmoney.jpg" alt="ANVOCRA old money — malikane" px={px} py={py} clip={clip}>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1b140c]/70 via-transparent to-black/30" />
        <motion.div style={{ opacity: oldFull }} className="absolute inset-0 bg-[#f4efe6]/10" />
        <div className="absolute right-5 md:right-10 bottom-24 md:bottom-14 text-right max-w-[46vw]">
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-[var(--ivory)] mb-4">{t("oldmoney")} / 02 ◦</p>
          {ready && (
            <RevealLines
              inView={false}
              delay={0.35}
              lines={[t("heroOld1"), t("heroOld2"), t("heroOld3")]}
              className="f-serif italic text-[var(--ivory)] leading-[0.95] text-[9vw] sm:text-6xl lg:text-[6.5rem]"
            />
          )}
        </div>
      </Layer>

      <motion.div style={{ left: seam }} className="absolute top-0 bottom-0 z-20 w-px -translate-x-1/2 bg-white/70 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border border-white/70 backdrop-blur-md bg-white/10 flex items-center justify-center gap-1 text-white">
          <ArrowLeft size={12} /><ArrowRight size={12} />
        </div>
      </motion.div>

      <div className="hidden sm:block absolute top-28 left-1/2 -translate-x-1/2 z-20 text-center pointer-events-none">
        <p className="f-mono text-[11px] uppercase tracking-[0.35em] text-white/90 mix-blend-difference">{t("season")}</p>
      </div>
      <div className="hidden md:block absolute bottom-6 left-1/2 -translate-x-1/2 z-20 f-mono text-[11px] uppercase tracking-[0.3em] text-white/80 pointer-events-none">
        ← {t("drag")} →
      </div>

      <div className="md:hidden absolute bottom-6 inset-x-5 z-20 flex gap-3">
        <button data-testid="hero-mobile-street-btn" onClick={(e) => { e.stopPropagation(); nav("/collections/streetwear"); }} className="flex-1 py-3 bg-[var(--acid)] text-black f-mono text-[11px] uppercase tracking-[0.2em]">{t("street")}</button>
        <button data-testid="hero-mobile-oldmoney-btn" onClick={(e) => { e.stopPropagation(); nav("/collections/old-money"); }} className="flex-1 py-3 bg-[var(--ivory)] text-[var(--navy)] f-serif italic text-[15px]">{t("oldmoney")}</button>
      </div>

      <HeroCursor cx={cx} cy={cy} side={side} visible={hover} />
      <div className="grain absolute inset-0 overflow-hidden pointer-events-none z-10" />
    </section>
  );
};
